import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  QrCode, Check, X, Store, Gift, AlertCircle, 
  Camera, KeyRound, ArrowLeft, Loader2, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Html5QrcodeScanner } from 'html5-qrcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RedeemPage = () => {
  const [step, setStep] = useState('pin'); // 'pin', 'scanner', 'confirm', 'success', 'error'
  const [pin, setPin] = useState('');
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.clear().catch(console.error);
      }
    };
  }, []);

  // Initialize scanner when step changes to 'scanner'
  useEffect(() => {
    if (step === 'scanner' && scannerRef.current && !html5QrCodeRef.current) {
      const scanner = new Html5QrcodeScanner(
        "qr-reader",
        { 
          fps: 10, 
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
          showTorchButtonIfSupported: true
        },
        false
      );

      scanner.render(onScanSuccess, onScanError);
      html5QrCodeRef.current = scanner;
    }

    return () => {
      if (step !== 'scanner' && html5QrCodeRef.current) {
        html5QrCodeRef.current.clear().catch(console.error);
        html5QrCodeRef.current = null;
      }
    };
  }, [step]);

  const onScanSuccess = async (decodedText) => {
    // Stop scanner
    if (html5QrCodeRef.current) {
      await html5QrCodeRef.current.clear();
      html5QrCodeRef.current = null;
    }

    // Extract voucher ID from URL or direct ID
    let voucherId = decodedText;
    if (decodedText.includes('/voucher/')) {
      voucherId = decodedText.split('/voucher/').pop();
    }

    // Fetch voucher info
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vouchers/public/${voucherId}`);
      setVoucher({ ...response.data, id: voucherId });
      setStep('confirm');
    } catch (err) {
      setError('Voucher no encontrado o ya canjeado');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const onScanError = (error) => {
    // Ignore scan errors (normal during scanning)
    console.debug('Scan error:', error);
  };

  const verifyPin = async () => {
    if (pin.length < 4) {
      toast.error('El PIN debe tener al menos 4 dígitos');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/locations/verify-pin`, { pin });
      setLocation(response.data);
      setStep('scanner');
      toast.success(`Acceso autorizado: ${response.data.name}`);
    } catch (err) {
      toast.error('PIN incorrecto. Verifica con tu encargado.');
    } finally {
      setLoading(false);
    }
  };

  const confirmRedeem = async () => {
    if (!voucher || !location) return;

    setLoading(true);
    try {
      await axios.post(`${API}/vouchers/redeem-by-pin`, { 
        voucher_id: voucher.id,
        pin: pin
      });
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al canjear el voucher');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('pin');
    setPin('');
    setLocation(null);
    setVoucher(null);
    setError('');
  };

  const goBackToScanner = () => {
    setVoucher(null);
    setError('');
    setStep('scanner');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">S</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Stampply para Locales</h1>
          <p className="text-slate-400 mt-1">Sistema de canje de premios</p>
        </div>

        {/* Step: PIN Entry */}
        {step === 'pin' && (
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Acceso para empleados</h2>
              <p className="text-slate-600 mt-2 text-sm">
                Introduce el PIN de tu local para canjear vouchers de clientes
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  PIN del local
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Ej: 4827"
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="redeem-pin-input"
                />
              </div>

              <Button
                className="w-full btn-primary"
                onClick={verifyPin}
                disabled={loading || pin.length < 4}
                data-testid="verify-pin-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Store className="w-5 h-5" />
                    Acceder
                  </>
                )}
              </Button>
            </div>

            {/* Help Section */}
            <div className="mt-6 pt-4 border-t border-slate-100">
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mx-auto"
              >
                <HelpCircle className="w-4 h-4" />
                ¿Dónde encuentro el PIN?
              </button>
              
              {showHelp && (
                <motion.div
                  className="mt-4 p-4 bg-blue-50 rounded-xl text-sm text-blue-800"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                >
                  <p className="font-medium mb-2">El PIN tiene doble función:</p>
                  <ul className="space-y-2 text-blue-700">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">1.</span>
                      <span>Los clientes lo usan para validar su visita si no tienen GPS</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">2.</span>
                      <span>Los empleados lo usan para acceder aquí y canjear premios</span>
                    </li>
                  </ul>
                  <p className="mt-3 text-blue-600">
                    Tu encargado o el dueño del negocio puede darte el PIN desde su panel de Stampply.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* Step: QR Scanner */}
        {step === 'scanner' && (
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={reset}
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Salir
              </button>
              <div className="flex items-center gap-2 text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">
                <Store className="w-4 h-4" />
                {location?.name}
              </div>
            </div>

            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Camera className="w-7 h-7 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Escanear voucher</h2>
              <p className="text-slate-600 mt-1 text-sm">
                Apunta la cámara al QR que muestra el cliente en su móvil
              </p>
            </div>

            <div 
              id="qr-reader" 
              ref={scannerRef}
              className="rounded-xl overflow-hidden border-2 border-dashed border-slate-200"
            />

            {loading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="ml-2 text-slate-600">Verificando voucher...</span>
              </div>
            )}

            <div className="mt-4 p-3 bg-slate-50 rounded-xl">
              <p className="text-xs text-slate-500 text-center">
                El cliente debe abrir el enlace del voucher que recibió por WhatsApp y mostrarte el código QR de su pantalla
              </p>
            </div>
          </motion.div>
        )}

        {/* Step: Confirm Redeem */}
        {step === 'confirm' && voucher && (
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="text-center mb-6">
              <motion.div 
                className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <Gift className="w-8 h-8 text-amber-600" />
              </motion.div>
              <h2 className="text-xl font-bold text-slate-900">¿Canjear este premio?</h2>
              <p className="text-slate-600 mt-1">Campaña: {voucher.campaign_name}</p>
            </div>

            <div className="bg-gradient-to-br from-primary to-indigo-600 rounded-xl p-6 text-white text-center mb-6">
              <p className="text-sm text-white/70 mb-1">Premio a entregar</p>
              <p className="text-2xl font-bold">{voucher.reward}</p>
            </div>

            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl mb-6">
              <Store className="w-5 h-5 text-slate-400" />
              <div className="text-sm">
                <p className="text-slate-500">Local que canjea:</p>
                <p className="font-medium text-slate-900">{location?.name}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={goBackToScanner}
                className="w-full"
              >
                <X className="w-4 h-4" />
                Cancelar
              </Button>
              <Button
                className="w-full btn-primary"
                onClick={confirmRedeem}
                disabled={loading}
                data-testid="confirm-redeem-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Canjear
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-xl text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <motion.div 
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
            >
              <Check className="w-10 h-10 text-green-600" />
            </motion.div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Voucher canjeado!</h2>
            <p className="text-slate-600 mb-2">Entrega el premio al cliente:</p>
            <div className="bg-green-50 text-green-800 rounded-xl p-4 mb-6">
              <p className="text-xl font-bold">{voucher?.reward}</p>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              El cliente recibirá una confirmación por WhatsApp
            </p>
            <Button
              className="w-full btn-primary"
              onClick={goBackToScanner}
              data-testid="scan-another-btn"
            >
              <QrCode className="w-5 h-5" />
              Escanear otro voucher
            </Button>
          </motion.div>
        )}

        {/* Step: Error */}
        {step === 'error' && (
          <motion.div
            className="bg-white rounded-2xl p-6 shadow-xl text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">No se pudo canjear</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button
              className="w-full"
              variant="outline"
              onClick={goBackToScanner}
            >
              <ArrowLeft className="w-4 h-4" />
              Intentar de nuevo
            </Button>
          </motion.div>
        )}

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6">
          Powered by Stampply · Sistema de fidelización
        </p>
      </motion.div>
    </div>
  );
};

export default RedeemPage;
